import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setFilteredMarketGroups, setSelectedMarketGroup } from '../../store/actions/markets';
import ContentSpinner from '../shared/Spinner/Spinner';
import Header from './Header/Header';
import Banner from './Banner/Banner';
import MarketBody from './MarketBody/MarketBody';
import { MARKET_GROUPS_ALL_ID } from '../../constants/defaultValues';
import { useHistory, useParams } from 'react-router-dom';
import { clearEventDetails, getEventDetails, setAllMarketsEventRemoved } from '../../store/actions/eventDetails';
import { getEventsUrl, removeMoreMarketsRedirectUrl } from '../../utils/helpers';
import { setSocketContext } from '../../store/actions/socketAction';
import ConnectionError from '../Sports/ConnectionError/ConnectionError';

import styles from './AllMarkets.module.scss';

const AllMarkets = props => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { sportRefId, eventId } = useParams();

    const decodedSportRefId = decodeURIComponent(sportRefId);
    const decodedEventId = decodeURIComponent(eventId);

    const { isSocketConnected, connectionCounter } = useSelector(state => state.socket);
    const { event: eventDetails, isEventRemoved } = useSelector(state => state.eventDetails);
    const marketGroups = useSelector(state => state.allMarkets.marketGroups);

    const { isLive } = useSelector(state => state.sportEvents);

    useEffect(() => {
        dispatch(setSocketContext({ allMarketsEventId: decodedEventId }));

        return () => {
            dispatch(setSocketContext({ allMarketsEventId: '' }));
            removeMoreMarketsRedirectUrl();
        };
    }, []);

    useEffect(() => {
        dispatch(getEventDetails(decodedEventId));

        return () => {
            dispatch(clearEventDetails());
            dispatch(setFilteredMarketGroups({ sportRefId: '' }));
            dispatch(setSelectedMarketGroup(MARKET_GROUPS_ALL_ID));
        };
    }, [decodedEventId]);

    useEffect(() => {
        dispatch(setFilteredMarketGroups({ sportRefId: decodedSportRefId }));
    }, [marketGroups, decodedEventId]);

    useEffect(() => {
        if (isEventRemoved) {
            const redirectUrl = getEventsUrl({ event: eventDetails });
            dispatch(clearEventDetails());
            dispatch(setAllMarketsEventRemoved(false));
            history.push(redirectUrl);
        }
    }, [isEventRemoved]);

    const moreMarkets = useMemo(() => {
        let formattedMarkets = [];

        if (eventDetails?.markets?.length > 0) {
            formattedMarkets = eventDetails.markets.reduce((result, market) => {
                const { marketRefId } = market;

                if (!result[marketRefId]) {
                    result[marketRefId] = [];
                }

                return { ...result, [marketRefId]: [...result[marketRefId], market] };
            }, {});
        }

        return formattedMarkets;
    }, [eventDetails]);

    const getMarketsBodyHeight = isLive => {
        return isLive ? 'calc(100vh - 205px)' : 'calc(100vh - 160px)';
    };

    return (
        <div className={styles.allMarkets} style={{ top: isLive ? '42px' : '0' }}>
            {!Object.keys(eventDetails).length || !connectionCounter ? (
                <ContentSpinner />
            ) : isSocketConnected ? (
                <>
                    <Header />
                    <div
                        style={{
                            overflow: 'auto',
                            height: getMarketsBodyHeight(isLive),
                            paddingRight: '10px',
                        }}
                    >
                        <Banner event={eventDetails} />
                        <MarketBody markets={moreMarkets} event={eventDetails} isLive={isLive} />
                    </div>
                </>
            ) : (
                <ConnectionError />
            )}
        </div>
    );
};

export default AllMarkets;
